/**
 * POS offline queue (WP-15.2, OS-0321): when the till has no connection, a ticket is kept here instead of being
 * lost, and replayed against `onesphere.api.pos.checkout` once the browser is back online. Storage is per-device
 * (localStorage, scoped by POS Profile) and per-viewer only — it is not synced between tills, which matches a
 * single cashier station losing and regaining its own connection.
 */
export type QueuedLine = { item_code: string; item_name: string; rate: number; qty: number };

export type QueuedTicket = {
  ref: string;
  profile: string;
  lines: QueuedLine[];
  mode_of_payment: string;
  customer?: string | null;
  queued_at: string;
  attempts: number;
  last_error?: string;
};

const KEY_PREFIX = "onesphere.pos.offline-queue.";

function storageKey(profile: string): string {
  return `${KEY_PREFIX}${profile}`;
}

export function newOfflineRef(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `pos-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function loadQueue(profile: string): QueuedTicket[] {
  if (!profile) return [];
  try {
    const raw = localStorage.getItem(storageKey(profile));
    return raw ? (JSON.parse(raw) as QueuedTicket[]) : [];
  } catch {
    return [];
  }
}

function saveQueue(profile: string, tickets: QueuedTicket[]): QueuedTicket[] {
  try {
    localStorage.setItem(storageKey(profile), JSON.stringify(tickets));
  } catch {
    // private window or storage full: the ticket still rings up for this page view, it just won't survive a reload
  }
  return tickets;
}

export function enqueueTicket(profile: string, ticket: Omit<QueuedTicket, "attempts">): QueuedTicket[] {
  return saveQueue(profile, [...loadQueue(profile), { ...ticket, attempts: 0 }]);
}

export function removeTicket(profile: string, ref: string): QueuedTicket[] {
  return saveQueue(profile, loadQueue(profile).filter((t) => t.ref !== ref));
}

export function recordAttemptFailure(profile: string, ref: string, error: string): QueuedTicket[] {
  return saveQueue(profile, loadQueue(profile).map((t) => (t.ref === ref ? { ...t, attempts: t.attempts + 1, last_error: error } : t)));
}
